import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@airia/ui';
import { Input } from '../../ui/input';
import { Slider } from '../../ui/slider';
import { Textarea } from '../../ui/textarea';
import { RefreshCw, Copy, Check, Pencil, Eye } from 'lucide-react';
import { isComposeMode } from '../../types/office';
import { formatEmailBody } from '../../lib/formatEmailBody';
import { stripHtml } from '../../lib/stripHtml';
import { parseEmailTags } from '../../lib/emailParser';
import { logger } from '../../lib/logger';
import { copyTextToClipboard } from '../../lib/clipboard';
const STORAGE_KEY = 'airia-rewrite-preferences';
const RewriteEmailForm = ({ onGenerate }) => {
    const [customInstructions, setCustomInstructions] = useState('');
    const [wordCount, setWordCount] = useState(30);
    const [tone, setTone] = useState('neutral');
    const [style, setStyle] = useState('formal');
    // Response preview state
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [error, setError] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState('');
    // Auto-reset isCopied after 2 seconds to prevent memory leak
    useEffect(() => {
        let timeout;
        if (isCopied) {
            timeout = setTimeout(() => setIsCopied(false), 2000);
        }
        return () => clearTimeout(timeout);
    }, [isCopied]);
    // Initialize edited content when AI response changes
    useEffect(() => {
        if (aiResponse) {
            const plainText = stripHtml(aiResponse);
            setEditedContent(plainText);
            setIsEditing(false);
            setError(null);
        }
    }, [aiResponse]);
    // Load saved preferences on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const prefs = JSON.parse(saved);
                setCustomInstructions(prefs.customInstructions || '');
                setWordCount(prefs.wordCount || 30);
                setTone(prefs.tone || 'neutral');
                setStyle(prefs.style || 'formal');
            }
        }
        catch (error) {
            console.error('Failed to load rewrite preferences:', error);
        }
    }, []);
    const buildRewritePrompt = () => {
        let prompt = 'Rewrite this email';
        if (customInstructions.trim()) {
            prompt += ` to make it more ${customInstructions.trim()}`;
        }
        if (wordCount < 200) {
            prompt += `. Keep it around ${wordCount} words`;
        }
        else {
            prompt += `. Make it as long as needed`;
        }
        prompt += `. Use a ${tone} tone`;
        prompt += ` and ${style} communication style`;
        return prompt;
    };
    const handleSubmit = () => {
        // Save preferences to localStorage
        const prefs = {
            customInstructions,
            wordCount,
            tone,
            style,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
        catch (error) {
            console.error('Failed to save rewrite preferences:', error);
        }
        // Reset state
        setIsGenerating(true);
        setAiResponse(null);
        setError(null);
        setIsCopied(false);
        // Build and send prompt with callback
        const prompt = buildRewritePrompt();
        onGenerate(prompt, (response) => {
            setIsGenerating(false);
            setAiResponse(response);
        });
    };
    const handleTryAgain = () => {
        // Simply re-submit with same parameters
        handleSubmit();
    };
    const handleEditParameters = () => {
        // Clear the response to go back to form
        setAiResponse(null);
        setError(null);
        setIsCopied(false);
    };
    const handleApplyToEmail = async () => {
        if (!aiResponse && !editedContent)
            return;
        setIsApplying(true);
        setError(null);
        try {
            if (typeof Office !== 'undefined' &&
                Office.context.mailbox &&
                Office.context.mailbox.item) {
                const item = Office.context.mailbox.item;
                if (!isComposeMode(item)) {
                    setError('This action is only available in compose mode');
                    setIsApplying(false);
                    return;
                }
                // If user has edited content, use that directly (plain text -> HTML)
                if (isEditing || editedContent !== stripHtml(aiResponse || '')) {
                    logger.log('RewriteEmailForm: Applying edited content');
                    if (item.body) {
                        const formattedBody = formatEmailBody(editedContent);
                        await new Promise((resolve) => {
                            item.body.setAsync(formattedBody, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
                                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                    logger.error('RewriteEmailForm: Error applying edited content:', asyncResult.error);
                                    setError('Failed to apply content to email');
                                }
                                resolve();
                            });
                        });
                        setIsApplying(false);
                    }
                    else {
                        setIsApplying(false);
                    }
                    return;
                }
                // Parse the AI response to check for airiaReplaceEmail tags
                const parts = parseEmailTags(aiResponse);
                logger.log('RewriteEmailForm: Parsed AI response parts:', parts);
                // Look for replace email data
                const replacePart = parts.find((p) => p.type === 'replace' && p.replaceEmailData);
                if (replacePart && replacePart.replaceEmailData) {
                    // Found airiaReplaceEmail tag - apply subject and body
                    const { subject, body } = replacePart.replaceEmailData;
                    logger.log('RewriteEmailForm: Found replace email data:', {
                        hasSubject: !!subject,
                        subject,
                        bodyLength: body.length,
                    });
                    let subjectUpdateFailed = false;
                    let bodyUpdateFailed = false;
                    // Update subject if provided
                    if (subject && item.subject) {
                        const subjectToSet = subject;
                        logger.log('RewriteEmailForm: Attempting to set subject:', subjectToSet);
                        await new Promise((resolve) => {
                            item.subject.setAsync(subjectToSet, (asyncResult) => {
                                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                    logger.error('RewriteEmailForm: Error setting subject:', asyncResult.error);
                                    subjectUpdateFailed = true;
                                }
                                else {
                                    logger.log('RewriteEmailForm: Subject set successfully:', subjectToSet);
                                }
                                resolve();
                            });
                        });
                    }
                    else {
                        logger.log('RewriteEmailForm: Subject update skipped. Has subject?', !!subject, 'Has item.subject?', !!item.subject);
                    }
                    // Apply the body content
                    if (item.body) {
                        const bodyToSet = formatEmailBody(body);
                        await new Promise((resolve) => {
                            item.body.setAsync(bodyToSet, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
                                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                    logger.error('RewriteEmailForm: Error applying email body:', asyncResult.error);
                                    bodyUpdateFailed = true;
                                }
                                else {
                                    logger.log('RewriteEmailForm: Email body applied successfully');
                                }
                                resolve();
                            });
                        });
                        // Update error state after both operations complete
                        setIsApplying(false);
                        if (subjectUpdateFailed && bodyUpdateFailed) {
                            setError('Failed to update both subject and content');
                        }
                        else if (subjectUpdateFailed) {
                            setError('Content updated, but subject update failed');
                        }
                        else if (bodyUpdateFailed) {
                            setError('Failed to apply content to email');
                        }
                    }
                    else {
                        setIsApplying(false);
                    }
                }
                else {
                    // No airiaReplaceEmail tag found - apply as plain text/HTML
                    logger.log('RewriteEmailForm: No replace email tag found, applying raw response');
                    if (item.body) {
                        // More robust HTML detection using regex to check for actual HTML tags
                        const isHtml = /<\/?[a-z][\s\S]*>/i.test(aiResponse);
                        const formattedBody = isHtml ? aiResponse : formatEmailBody(aiResponse);
                        await new Promise((resolve) => {
                            item.body.setAsync(formattedBody, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
                                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                    logger.error('RewriteEmailForm: Error applying email content:', asyncResult.error);
                                    setError('Failed to apply content to email');
                                }
                                resolve();
                            });
                        });
                        setIsApplying(false);
                    }
                    else {
                        setIsApplying(false);
                    }
                }
            }
            else {
                setIsApplying(false);
            }
        }
        catch (err) {
            logger.error('RewriteEmailForm: Error applying to email:', err);
            setError('An unexpected error occurred');
            setIsApplying(false);
        }
    };
    const handleCopyToClipboard = async () => {
        if (!editedContent && !aiResponse)
            return;
        try {
            // Use edited content if available, otherwise strip HTML from AI response
            const textToCopy = editedContent || stripHtml(aiResponse || '');
            await copyTextToClipboard(textToCopy);
            setIsCopied(true);
            // Note: Reset is handled by useEffect to prevent memory leak
        }
        catch (err) {
            console.error('Error copying to clipboard:', err);
            setError('Failed to copy to clipboard');
        }
    };
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    return (_jsxs("div", { className: "my-4 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Make my message more..." }), _jsx(Input, { type: "text", placeholder: "shorter, professional, detailed", value: customInstructions, onChange: (e) => setCustomInstructions(e.target.value), className: "w-full", disabled: isGenerating })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Email Length" }), _jsx(Slider, { value: [wordCount], onValueChange: ([value]) => setWordCount(value), min: 30, max: 200, step: 10, className: "w-full" }), _jsx("div", { className: "text-sm text-muted-foreground", children: wordCount === 200 ? '200+ words' : `${wordCount} words` })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Choose Tone" }), _jsx("div", { className: "flex gap-2", children: ['positive', 'neutral', 'negative'].map((t) => (_jsx(Button, { variant: tone === t ? 'default' : 'outline', size: "sm", onClick: () => setTone(t), className: "flex-1", disabled: isGenerating, children: capitalize(t) }, t))) })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Communication Style" }), _jsx("div", { className: "flex gap-2", children: ['formal', 'informal', 'factual'].map((s) => (_jsx(Button, { variant: style === s ? 'default' : 'outline', size: "sm", onClick: () => setStyle(s), className: "flex-1", disabled: isGenerating, children: capitalize(s) }, s))) })] }), !aiResponse && (_jsx(Button, { onClick: handleSubmit, className: "w-full", size: "sm", disabled: isGenerating, children: isGenerating ? (_jsx("span", { children: "Generating..." })) : (_jsxs(_Fragment, { children: [_jsx(RefreshCw, { className: "size-4 mr-2" }), "Rewrite Email"] })) })), aiResponse && (_jsxs("div", { className: "mt-4 space-y-3", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "text-xs font-medium text-muted-foreground", children: "AI Response:" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => setIsEditing(!isEditing), className: "h-7 px-2 text-xs", children: isEditing ? (_jsxs(_Fragment, { children: [_jsx(Eye, { className: "size-3 mr-1" }), "Preview"] })) : (_jsxs(_Fragment, { children: [_jsx(Pencil, { className: "size-3 mr-1" }), "Edit"] })) })] }), _jsx("div", { className: "p-3 bg-background border border-border rounded-md", children: isEditing ? (_jsx(Textarea, { value: editedContent, onChange: (e) => setEditedContent(e.target.value), className: "min-h-[150px] text-sm resize-none border-0 p-0 focus-visible:ring-0", placeholder: "Edit your draft here..." })) : (_jsx("div", { className: "text-sm text-foreground whitespace-pre-wrap", children: editedContent })) }), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: handleTryAgain, className: "w-full", children: [_jsx(RefreshCw, { className: "size-4 mr-1" }), "Try Again"] }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleEditParameters, className: "w-full", children: "Edit Parameters" })] }), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsx(Button, { variant: "default", size: "sm", onClick: handleApplyToEmail, className: "w-full", disabled: isApplying, children: isApplying ? 'Applying...' : 'Apply to Email' }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleCopyToClipboard, className: "w-full", disabled: isApplying, children: isCopied ? (_jsxs(_Fragment, { children: [_jsx(Check, { className: "size-4 mr-1" }), "Copied!"] })) : (_jsxs(_Fragment, { children: [_jsx(Copy, { className: "size-4 mr-1" }), "Copy"] })) })] })] })), error && (_jsx("div", { className: "p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive", children: error }))] }));
};
export default RewriteEmailForm;
